import * as React from 'react';

import { cx } from '../../utils/cx';
import CollapseButton from '../base/buttons/CollapseButton';

interface IWidgetContextValue {
  isCollapsed: boolean;
  onToggle: () => void;
}

const WidgetContext = React.createContext<IWidgetContextValue | null>(null);

interface IWidgetProps extends React.ComponentPropsWithoutRef<'div'> {
  children: React.ReactNode;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

const Widget = ({ children, className, collapsible = false, defaultCollapsed = false, ...props }: IWidgetProps) => {
  const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed);

  const onToggle = React.useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const contextValue = React.useMemo<IWidgetContextValue>(
    () => ({
      isCollapsed,
      onToggle,
    }),
    [isCollapsed, onToggle]
  );

  return (
    <WidgetContext.Provider value={collapsible ? contextValue : null}>
      <div
        className={cx('bg-secondary_subtle shadow-xs flex flex-col overflow-hidden rounded-2xl', className)}
        {...props}
      >
        {children}
      </div>
    </WidgetContext.Provider>
  );
};

interface IWidgetHeaderProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'title'> {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  trailing?: React.ReactNode;
}

const WidgetHeader = ({ title, subtitle, trailing, className, ...props }: IWidgetHeaderProps) => {
  const context = React.useContext(WidgetContext);
  const isCollapsible = context !== null;

  return (
    <div
      className={cx(
        'bg-secondary border-tertiary flex items-center justify-between border-b p-5',
        isCollapsible && 'py-4.25 px-5',
        className
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-1 flex-col items-start justify-center">
        <div className="text-primary truncate text-lg font-semibold">{title}</div>
        {subtitle && <div className="text-tertiary truncate text-sm font-normal">{subtitle}</div>}
      </div>
      <div className="flex shrink-0 items-center justify-center gap-2 self-center">
        {trailing}
        {isCollapsible && context && (
          <CollapseButton isCollapsed={context.isCollapsed} onToggle={() => context.onToggle()} />
        )}
      </div>
    </div>
  );
};

interface IWidgetBodyProps extends React.ComponentPropsWithoutRef<'div'> {
  children: React.ReactNode;
}

const WidgetBody = ({ children, className, ...props }: IWidgetBodyProps) => {
  const context = React.useContext(WidgetContext);
  const isCollapsed = context?.isCollapsed ?? false;

  if (isCollapsed) {
    return null;
  }

  return (
    <div className={cx('flex flex-col', className)} {...props}>
      {children}
    </div>
  );
};

interface IWidgetRowProps extends React.ComponentPropsWithoutRef<'div'> {
  label: React.ReactNode;
  value?: React.ReactNode;
  valueClassName?: string;
  icon?: React.ReactNode;
  subline?: React.ReactNode;
}

const WidgetRow = ({ label, value, valueClassName, icon, subline, className, ...props }: IWidgetRowProps) => {
  const labelId = React.useId();
  return (
    <div className={cx('flex items-center justify-between gap-5 px-5 py-3', className)} {...props}>
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
        {icon && <div className="shrink-0">{icon}</div>}
        <div id={labelId} className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {label}
          {subline && <div className="text-tertiary truncate text-sm font-normal">{subline}</div>}
        </div>
      </div>
      {value && (
        <span
          aria-labelledby={labelId}
          className={cx('text-md shrink-0 font-medium', valueClassName || 'text-primary')}
        >
          {value}
        </span>
      )}
    </div>
  );
};

Widget.Header = WidgetHeader;
Widget.Body = WidgetBody;
Widget.Row = WidgetRow;

export default Widget;
